import React, { useEffect, useRef, useState } from "react";
import {
  FaWhatsapp,
  FaCheck,
  FaArrowRight,
  FaDumbbell,
  FaHeartbeat,
  FaRunning,
  FaUsers,
} from "react-icons/fa";
import About_1 from "../assets/About/About_1.png";
import About_2 from "../assets/About/About_2.png";
import About_3 from "../assets/About/About_3.png";
import About_4 from "../assets/About/About_4.png";
import Seo from "../seo/Seo";
import { seoConfig } from "../seo/seoConfig";
import "../css/Home.css";

// Service Card Component with animation
const ServiceCard = ({
  icon: Icon,
  image,
  title,
  description,
  delay,
  link,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={cardRef}
      className={`home-service-card ${isVisible ? "visible" : ""}`}
    >
      <div className="service-card-icon">
        {image ? <img src={image} alt={title} /> : <Icon />}
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {link && (
        <a href={link} className="service-card-link">
          Saber Mais <FaArrowRight />
        </a>
      )}
    </div>
  );
};

// Stats Card Component
const StatCard = ({ number, label, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={cardRef} className={`stat-card ${isVisible ? "visible" : ""}`}>
      <span className="stat-number">{number}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
};

const Home = () => {
  return (
    <div className="home-page">
      <Seo {...seoConfig.home} />
      {/* Hero Section with Full Image Background */}
      <section className="home-hero">
        <div className="hero-background">
          <img
            src="/home_logo.jpg"
            alt="Interior do ginásio Life Center em Lisboa"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <h1>
              O Seu Caminho Para Uma{" "}
              <span className="highlight">Vida Melhor</span>
            </h1>
            <p className="hero-description">
              Centro de excelência em saúde, exercício e bem-estar. Transforme o
              seu corpo e mente com profissionais certificados num ambiente
              acolhedor e motivador.
            </p>
            <div className="hero-cta">
              <a
                href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Gostaria de saber mais sobre o Life Center.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button primary"
              >
                <FaWhatsapp /> Marcar Consulta
              </a>
              <a href="/modalidades" className="cta-button secondary">
                <span>Explorar Modalidades</span>
                <FaArrowRight />
              </a>
            </div>
            <div className="hero-features">
              <div className="hero-feature">
                <FaCheck /> Avaliação Gratuita
              </div>
              <div className="hero-feature">
                <FaCheck /> Planos Personalizados
              </div>
              <div className="hero-feature">
                <FaCheck /> Equipa Certificada
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator">
          <span>Scroll</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="services-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">O Que Fazemos</span>
            <h2>Serviços de Excelência</h2>
            <p className="section-description">
              Oferecemos uma gama completa de serviços para o seu bem-estar
              físico e mental.
            </p>
          </div>

          <div className="services-grid">
            <ServiceCard
              image={About_1}
              title="Exercício Físico"
              description="A importância da prática de exercício físico faz-nos apostar num serviço diferenciado e de proximidade. Tratamos cada caso com a singularidade que merece."
              delay={0}
              link="/modalidades"
            />
            <ServiceCard
              image={About_2}
              title="Triathlon Coaching"
              description="Coaching especializado para ajudá-lo a atingir os seus objetivos. Plano de treino personalizado e acompanhamento contínuo do progresso."
              delay={100}
              link="/triathlon-coaching"
            />
            <ServiceCard
              image={About_3}
              title="Clínica & Terapias"
              description="Desde análises clínicas passando pela Osteopatia, Nutrição Funcional, Fisioterapia e Massagem Desportiva. A sua saúde é a nossa prioridade."
              delay={200}
              link="/terapias"
            />
            <ServiceCard
              image={About_4}
              title="Clientes Empresariais"
              description="Colaboradores saudáveis traduzem empresas felizes. Apostamos num serviço que cumpra a análise, diagnóstico e avaliação das necessidades."
              delay={300}
              link="/clientes-empresariais"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <StatCard number="10+" label="Anos de Experiência" delay={0} />
            <StatCard number="5000+" label="Clientes Satisfeitos" delay={100} />
            <StatCard number="20+" label="Modalidades" delay={200} />
            <StatCard number="15+" label="Profissionais" delay={300} />
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-section">
        <div className="container">
          <div className="why-grid">
            <div className="why-content">
              <div className="section-header left">
                <span className="section-tag">Porquê Escolher-nos</span>
                <h2>A Sua Saúde é a Nossa Prioridade</h2>
              </div>
              <p className="why-description">
                No Life Center, acreditamos que cada pessoa merece um
                atendimento personalizado e de qualidade. A nossa equipa de
                profissionais certificados está pronta para ajudá-lo a alcançar
                os seus objetivos.
              </p>
              <ul className="why-features">
                <li>
                  <FaCheck className="check-icon" />
                  <span>Profissionais Certificados e Experientes</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Equipamentos Modernos e de Qualidade</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Ambiente Acolhedor e Motivador</span>
                </li>
                <li>
                  <FaCheck className="check-icon" />
                  <span>Planos Personalizados às Suas Necessidades</span>
                </li>
              </ul>
              <a
                href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Gostaria de agendar uma visita ao Life Center.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button primary"
              >
                <FaWhatsapp /> Agende Uma Visita
              </a>
            </div>
            <div className="why-icons">
              <div className="icon-card">
                <FaDumbbell />
                <span>Treino</span>
              </div>
              <div className="icon-card">
                <FaHeartbeat />
                <span>Saúde</span>
              </div>
              <div className="icon-card">
                <FaRunning />
                <span>Performance</span>
              </div>
              <div className="icon-card">
                <FaUsers />
                <span>Comunidade</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Pronto para Começar a Sua Transformação?</h2>
            <p>
              Junte-se a milhares de pessoas que já transformaram as suas vidas
              no Life Center.
            </p>
            <a
              href={`https://wa.me/+351962281179?text=${encodeURIComponent("Olá! Quero começar a minha transformação no Life Center!")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button primary large"
            >
              <FaWhatsapp /> Comece Hoje
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
